import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import { motion } from "framer-motion";
import { Camera, Books, ChartLineUp } from "@phosphor-icons/react";

const KIND_META = {
  photo: { icon: Camera, color: "var(--vs-primary)", label: "Activity photo" },
  marksheet: { icon: Books, color: "var(--vs-secondary)", label: "Marksheet published" },
  progress: { icon: ChartLineUp, color: "var(--vs-info)", label: "Monthly progress" },
};

export default function DonorFeed() {
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/donor/feed")
      .then((r) => setFeed(r.data || []))
      .catch(() => setFeed([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout title="Activity Feed" subtitle="Latest photos, published marksheets and progress reports for your sponsored students.">
      <div className="max-w-3xl">
        {loading && <div className="text-sm text-[var(--vs-text-secondary)]">Loading feed…</div>}
        {!loading && feed.length === 0 && (
          <div className="vs-card p-8 text-center text-sm text-[var(--vs-text-secondary)]">
            No activity yet. As soon as a hostel or school posts a photo, marksheet or progress, it'll appear here.
          </div>
        )}
        <div className="space-y-3">
          {feed.map((it, i) => {
            const meta = KIND_META[it.kind] || { icon: ChartLineUp, color: "var(--vs-secondary)", label: it.kind };
            const Icon = meta.icon;
            return (
              <motion.div
                key={`${it.kind}-${it.id}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: i * 0.03 }}
                data-testid={`feed-item-${it.kind}-${it.id}`}
                className="vs-card p-4 flex items-start gap-4"
              >
                <div
                  className="w-10 h-10 rounded-md grid place-items-center flex-shrink-0"
                  style={{ background: `${meta.color}20`, color: meta.color }}
                >
                  <Icon size={18} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--vs-text-secondary)]">{meta.label}</div>
                    <div className="font-mono text-[11px] text-[var(--vs-text-secondary)] flex-shrink-0">{new Date(it.created_at).toLocaleString()}</div>
                  </div>
                  <div className="font-display text-base font-semibold mt-1 truncate">{it.title}</div>
                  <div className="text-xs text-[var(--vs-text-secondary)] mt-0.5">
                    {it.student_name} · <span className="font-mono">{it.student_vs_id}</span>
                    {it.subtitle ? <> · {it.subtitle}</> : null}
                  </div>
                  {it.kind === "photo" && it.storage_path && (
                    <a
                      href={`${API}/files/photo?path=${encodeURIComponent(it.storage_path)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="block mt-3 max-w-md rounded-md overflow-hidden border border-[var(--vs-border)]"
                    >
                      <img
                        src={`${API}/files/photo?path=${encodeURIComponent(it.storage_path)}`}
                        alt={it.title}
                        className="w-full h-auto object-cover"
                        loading="lazy"
                      />
                    </a>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
}
